import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { DEO_TARGET_ID, TalkingToTarget } from '../../models/talking-to-target.model';

/**
 * Who the widget is talking to: DEO itself, or one named agent.
 *
 * Presentational — the widget owns the list and the selection, this renders them and reports a
 * pick. Agent rows are the shared `<gains-agent-card variant="compact">`, whose geometry already
 * matches the design's row exactly (24px mark, name, `autonomy · reversibility`); only DEO's row is
 * hand-rolled, because DEO is not an `AgentDefinition` and keeps its own mark colours.
 *
 * Dismissal lives here, as it does in `<gains-tray>` and the Active agents popover: Escape and a
 * mousedown outside both close the panel. This does not fight the widget's own outside-click
 * handler — a click inside the widget but outside this panel closes only the panel, and a click
 * outside the widget closes both, which is what you would expect either way.
 */
@Component({
  selector: 'deo-talking-to-selector',
  standalone: false,
  templateUrl: './deo-talking-to-selector.component.html',
  styleUrl: './deo-talking-to-selector.component.scss'
})
export class DeoTalkingToSelectorComponent {
  @Input() targets: TalkingToTarget[] = [];

  @Input() selectedId: string = DEO_TARGET_ID;

  @Output() selected$ = new EventEmitter<TalkingToTarget>();

  readonly caption = 'Talking to';

  /**
   * DEO's own mark, the same asset the launcher button uses.
   *
   * The agents are identified by their initials on an accent square; DEO is identified by the icon
   * a reader already associates with the widget, rather than by the letters "DEO".
   */
  readonly deoIcon = 'assets/icons/deo-agent-icon.svg';

  isOpen = false;

  constructor(private readonly _elementRef: ElementRef<HTMLElement>) {}

  /** The row currently in force, falling back to DEO so the trigger always has a name to show. */
  get selectedTarget(): TalkingToTarget | undefined {
    return this.targets.find(target => target.id === this.selectedId) ?? this.targets[0];
  }

  get isDeoSelected(): boolean {
    return (this.selectedTarget?.id ?? DEO_TARGET_ID) === DEO_TARGET_ID;
  }

  isDeo(target: TalkingToTarget): boolean {
    return target.id === DEO_TARGET_ID;
  }

  isCurrent(target: TalkingToTarget): boolean {
    return target.id === this.selectedTarget?.id;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  select(target: TalkingToTarget): void {
    this.close();

    if (target.id !== this.selectedId) {
      this.selected$.emit(target);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.isOpen && this.isOutside(event.target)) {
      this.close();
    }
  }

  protected trackById(index: number, target: TalkingToTarget): string {
    return target.id;
  }

  private isOutside(target: EventTarget | null): boolean {
    return target instanceof Node && !this._elementRef.nativeElement.contains(target);
  }
}
